import React, {
    ChangeEventHandler,
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import { observer } from "mobx-react";
import styled from "styled-components";

import { defined } from "@formant/core/common/defined";
import { narrowError } from "@formant/core/common/narrowError";
import { ResponseError } from "@formant/core/common/ResponseError";
import { IRole } from "@formant/core/model/IRole";
import { ISsoConfiguration } from "@formant/core/model/ISsoConfiguration";
import { ISsoGroupToTeamMapping } from "@formant/core/model/ISsoGroupToTeamMapping";
import { ITeam } from "@formant/core/model/ITeam";
import { IValidation } from "@formant/core/model/IValidation";
import { isSsoAuthenticationFlow } from "@formant/core/model/SsoAuthenticationFlow";
import { ssoAuthenticationFlows } from "@formant/core/model/ssoAuthenticationFlows";
import { Icon } from "@formant/ui/component/common/Icon";
import { IPickerGroup } from "@formant/ui/component/picker";
import { useToggle } from "@formant/ui/utils/useToggle";

import { useStores } from "../../../context/StoreContext";
import { showMessenger } from "../../../utils/intercom";
import CommonGroupPicker from "../../common/CommonGroupPicker/CommonGroupPicker";
import CommonSelectPicker from "../../common/CommonSelectPicker/CommonSelectPicker";
import DialogWithTabs, {
    IDialogOptions,
} from "../../common/DialogWithTabs/DialogWithTabs";
import { Heading2 } from "../../common/Heading2";
import { Switch } from "../../common/Switch";

const FormLabelContainer = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 0.625rem;
    color: var(--primary-silver, #bac4e2);
    text-align: right;
    font-size: 13px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: 0.6px;
`;

const FormInputContainer = styled.div`
    margin-bottom: 2.813rem;
    color: var(--primary-silver, #bac4e2);
    font-size: 13px;
    padding-left: 8px;
`;

const SectionLabel = styled(Heading2)`
    margin: 1.875rem 0;
    margin-top: 4.063rem;
    color: var(--primary-silver, #bac4e2);
    font-size: 13px;
    font-weight: 700;
    line-height: 22px;
    letter-spacing: 1.1px;
    text-transform: uppercase;
`;

const StyledP = styled.p`
    margin: 0;
    color: var(--primary-silver, #bac4e2);
    font-size: 13px;
`;

const StyledSwitch = styled(Switch)`
    line-height: 20px;
    padding-bottom: 5px;
    border-bottom: 2px solid var(--formant-color-primary-steel03);
`;

const StyledDomainError = styled.p`
    color: var(--formant-color-severity-levels-red);
    display: flex;
    gap: 3px;
    align-items: center;
`;

const StyledFormGroupToSsoContainer = styled.div<{ isAddSection?: boolean }>`
    display: grid;
    grid-template-columns: 40% 40% 20%;
    align-items: center;
    font-size: 12px;
    justify-content: flex-start;
    height: 48px;
    border-bottom: 1px solid var(--primary-steel-02, #3b4668);
    background: ${props =>
        props.isAddSection ? "none" : "var(--primary-module, #2d3855)"};
    cursor: ${props => (props.isAddSection ? "pointer" : "auto")};
    padding-left: 10px;
`;

const StyledTextField = styled(TextField)<{
    isSsoGroup?: boolean;
}>`
    padding-left: ${props => (props.isSsoGroup ? "10px" : "")};
    padding-right: ${props => (props.isSsoGroup ? "20px" : "")};

    .MuiInputBase-root {
        overflow: hidden;
        color: var(--primary-module, #fff);
        text-overflow: ellipsis;
        white-space: nowrap;
        font-size: 13px;
        line-height: 20px;
        letter-spacing: 0.6px;
        border-bottom: ${props => (props.isSsoGroup ? "none !important" : "")};

        &::before {
            border-bottom: ${props =>
                props.isSsoGroup ? "none !important" : ""};
            border-bottom-style: solid;
        }
    }
`;

const defaultEmptyTeam: ITeam = {
    id: "-1",
    roleId: "-1",
    name: "None (individual user)",
    tags: {},
};

const oidcDocsUrl =
    "https://docs.formant.io/docs/configure-openid-connect-oidc-sso";
const googleDocsUrl = "https://docs.formant.io/docs/google-sso";

interface IFormHelperIconProps {
    children: string;
    href?: string;
}

// TODO: replace with unified implementations of <Help> and <Tooltip> components once they are available
function FormHelperIcon(props: IFormHelperIconProps) {
    const { children: title, href } = props;
    return (
        <Tooltip title={title}>
            <IconButton
                {...(href && {
                    href,
                    target: "_blank",
                    rel: "noopener noreferrer",
                })}
                style={{ padding: 0 }}
            >
                <Icon name="help" />
            </IconButton>
        </Tooltip>
    );
}

function useFormInputState(
    initialValue: string,
): [string, ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>] {
    const [value, setValue] = useState(initialValue);
    const onInput = useCallback<
        ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>
    >(e => setValue(e.target.value), [setValue]);
    return [value, onInput];
}

interface IInitialSsoSetting {
    settings?: ISsoConfiguration;
    initialTeam?: ITeam;
    initialRole?: IRole;
}

interface ISsoDialogProps {
    enableSsoOidc: boolean;
    initialDomain: string;
    initialGoogleSSOEnabled: boolean;
    initialOidcSettings?: IInitialSsoSetting;
    initialSelectedRole: IRole | undefined;
    initialSelectedTeam: ITeam | undefined;
    roles: IRole[];
    setToggleSso: (value: boolean) => void;
    ssoAuthFlows: ISsoConfiguration[];
    teams: ITeam[];
    toggleSso: boolean;
}

function SsoDialog(props: ISsoDialogProps) {
    const {
        enableSsoOidc,
        initialDomain,
        initialGoogleSSOEnabled,
        initialOidcSettings,
        initialSelectedRole,
        initialSelectedTeam,
        roles,
        setToggleSso,
        ssoAuthFlows,
        teams,
        toggleSso,
    } = props;

    const { ssoStore, userStore } = useStores();

    const authenticatedUser = userStore.authenticated();

    let usersDomain: string | undefined;
    if (authenticatedUser) {
        usersDomain = authenticatedUser.email?.split("@").pop();
    }

    // OIDC states
    const [selectedOIDCTeam, setSelectedOIDCTeam] = useState<ITeam | undefined>(
        initialOidcSettings?.initialTeam,
    );

    const [selectedOIDCRole, setSelectedOIDCRole] = useState<IRole | undefined>(
        initialOidcSettings?.initialRole,
    );

    const [ssoGroupToTeam, setSsoGroupToTeam] = useState<
        ISsoGroupToTeamMapping[] | undefined
    >(
        initialOidcSettings?.settings?.ssoGroupNameToTeamMappings
            ? initialOidcSettings?.settings?.ssoGroupNameToTeamMappings.map(
                  (value, idx) => ({ ...value, id: idx }),
              )
            : [],
    );

    const lastGroupToTeamSsoRef = useRef<HTMLInputElement | null>(null);

    const [oidcDomain, setOidcDomain] = useFormInputState(
        initialOidcSettings?.settings?.domain || usersDomain || "",
    );

    const [oidcSSOEnabled, { toggle: onToggleOidcSso, off: onOffOidcSso }] =
        useToggle(!!initialOidcSettings?.settings?.enabled);

    const [oidcIssuer, setOidcIssuer] = useFormInputState(
        initialOidcSettings?.settings?.issuer || "",
    );

    const [oidcClientId, setOidcClientId] = useFormInputState(
        initialOidcSettings?.settings?.clientId || "",
    );

    const [clickedAddSSoGroup, setClickedAddSsoGroup] =
        useState<boolean>(false);

    // Google states
    const [isSavingSso, setIsSavingSso] = useState(false);
    const [isDomainDuplicated, setIsDomainDuplicated] = useState(false);
    const [validationErrors, setValidationErrors] = useState<
        IValidation | undefined
    >(undefined);

    const [selectedTeam, setSelectedTeam] = useState<ITeam | undefined>(
        initialSelectedTeam,
    );
    const [selectedRole, setSelectedRole] = useState<IRole | undefined>(
        initialSelectedRole,
    );

    const [domain, setDomain] = useFormInputState(initialDomain);
    const [
        googleSSOEnabled,
        { toggle: onToggleGoogleSso, off: onOffGoogleSso },
    ] = useToggle(initialGoogleSSOEnabled);

    useEffect(() => {
        if (clickedAddSSoGroup && lastGroupToTeamSsoRef.current) {
            lastGroupToTeamSsoRef.current.focus();
            setClickedAddSsoGroup(false);
        }
    }, [
        ssoGroupToTeam,
        lastGroupToTeamSsoRef,
        clickedAddSSoGroup,
        setClickedAddSsoGroup,
    ]);

    const toggleSsoOff = useCallback(() => {
        setToggleSso(false);
    }, [setToggleSso]);

    const handleSetSelectedTeam = useCallback(
        (team: ITeam) => {
            const roleSelected = roles.find(val => val.id === team.roleId);
            setSelectedTeam(team);
            setSelectedRole(roleSelected);
        },
        [setSelectedTeam, setSelectedRole, roles],
    );

    const handleSetSelectedRole = useCallback(
        (role: IRole) => {
            setSelectedRole(role);
            setSelectedTeam(defaultEmptyTeam);
        },
        [setSelectedTeam, setSelectedRole],
    );

    const handleSetSelectedOidcTeam = useCallback(
        (team: ITeam) => {
            setSelectedOIDCTeam(team);
            setSelectedOIDCRole(undefined);
        },
        [setSelectedOIDCTeam, setSelectedOIDCRole, roles],
    );

    const handleSetSelectedOidcRole = useCallback(
        (role: IRole) => {
            setSelectedOIDCRole(role);
            setSelectedOIDCTeam(undefined);
        },
        [setSelectedOIDCTeam, setSelectedOIDCRole],
    );

    const handleOnDeleteSsoGroupClick = useCallback(
        (index: number | undefined) => {
            if (typeof index !== "undefined") {
                const filteredMap = ssoGroupToTeam?.filter(
                    (_, i) => i !== index,
                );
                setSsoGroupToTeam(filteredMap);
            }
        },
        [ssoGroupToTeam, setSsoGroupToTeam],
    );

    const handleOnAddSsoGroup = useCallback(() => {
        const newSsoGroup: ISsoGroupToTeamMapping = {
            id: ssoGroupToTeam?.length ? ssoGroupToTeam.length : 0,
            ssoGroupName: "New group",
            teamId: defaultEmptyTeam?.id || "",
        };

        let updatedMap: ISsoGroupToTeamMapping[] = [];

        if (ssoGroupToTeam === undefined) {
            updatedMap = [newSsoGroup];
        } else {
            updatedMap = ssoGroupToTeam.concat(newSsoGroup);
        }

        setSsoGroupToTeam([...updatedMap]);
        setClickedAddSsoGroup(true);
    }, [ssoGroupToTeam, setSsoGroupToTeam]);

    const handleOnChangeSsoGroup = useCallback(
        (
            index: number | undefined,
            key: keyof ISsoGroupToTeamMapping,
            value: string | undefined,
        ) => {
            if (!ssoGroupToTeam?.length) {
                const newSsoGroup: ISsoGroupToTeamMapping =
                    {} as ISsoGroupToTeamMapping;
                return setSsoGroupToTeam([
                    Object.assign(newSsoGroup, { id: 0, [key]: value }),
                ]);
            }

            if (typeof index !== "undefined") {
                const updatedGroups = [...ssoGroupToTeam];
                const updatedGroup = { ...updatedGroups[index] };
                updatedGroups[index] = Object.assign(updatedGroup, {
                    [key]: value,
                });

                return setSsoGroupToTeam(updatedGroups);
            }

            return;
        },
        [ssoGroupToTeam, setSsoGroupToTeam],
    );

    const handleChangeGoogleSso = useCallback(() => {
        onToggleGoogleSso();
        onOffOidcSso();
    }, [onToggleGoogleSso, onOffOidcSso]);

    const handleChangeOidcSso = useCallback(() => {
        onToggleOidcSso();
        onOffGoogleSso();
    }, [onToggleOidcSso, onOffGoogleSso]);

    const googleTabContent = (
        <>
            <FormLabelContainer>
                <p>Google SSO</p>
                <FormHelperIcon href={googleDocsUrl}>
                    Enable Google SSO to allow new users to sign up and sign in
                    using Google.
                </FormHelperIcon>
            </FormLabelContainer>

            <StyledSwitch
                state={googleSSOEnabled}
                onChange={handleChangeGoogleSso}
                label={googleSSOEnabled ? "On" : "Off"}
            />

            {googleSSOEnabled ? (
                <>
                    <SectionLabel>Access</SectionLabel>

                    <FormInputContainer>
                        <FormLabelContainer>
                            <StyledP>Domain</StyledP>
                            <FormHelperIcon href={googleDocsUrl}>
                                Users with a Google email address on this domain
                                will be able to sign in via SSO. This must match
                                your email address in Formant. For example, if
                                your email is admin@xyz.com, you can only add
                                xyz.com."
                            </FormHelperIcon>
                        </FormLabelContainer>

                        <StyledTextField
                            id="standard-basic"
                            placeholder="Edit"
                            variant="standard"
                            fullWidth={true}
                            value={domain}
                            onChange={setDomain}
                            helperText={
                                validationErrors?.domain ? (
                                    validationErrors.domain
                                ) : isDomainDuplicated ? (
                                    <StyledDomainError>
                                        Already configured for Google SSO.
                                        <Link
                                            component="button"
                                            onClick={showMessenger}
                                        >
                                            Contact support
                                        </Link>
                                    </StyledDomainError>
                                ) : null
                            }
                            error={!!validationErrors?.domain}
                        />
                    </FormInputContainer>

                    <FormInputContainer>
                        <FormLabelContainer>
                            <StyledP>Default team</StyledP>
                            <FormHelperIcon href={googleDocsUrl}>
                                Choose the default team that this user should be
                                part of. Either a default team or a default role
                                should be selected.
                            </FormHelperIcon>
                        </FormLabelContainer>

                        <CommonSelectPicker<ITeam>
                            onSetSelected={handleSetSelectedTeam}
                            optionIdKey="id"
                            optionLabelKey="name"
                            options={[defaultEmptyTeam, ...teams]}
                            selectedOption={selectedTeam}
                            selectProps={{
                                placeholder: "Select",
                            }}
                            helperTextProps={{
                                error: !!validationErrors?.defaultTeamId,
                                label: validationErrors?.defaultTeamId
                                    ? validationErrors.defaultTeamId[0]
                                    : "",
                            }}
                        />
                    </FormInputContainer>

                    <FormInputContainer>
                        <FormLabelContainer>
                            <StyledP>Default role</StyledP>
                            <FormHelperIcon href={googleDocsUrl}>
                                Choose the default role that a user that signs
                                up should be assigned. Either a default team or
                                a default role should be selected.
                            </FormHelperIcon>
                        </FormLabelContainer>

                        <CommonSelectPicker<IRole>
                            onSetSelected={handleSetSelectedRole}
                            optionIdKey="id"
                            optionLabelKey="name"
                            options={roles || []}
                            selectedOption={selectedRole}
                            selectProps={{
                                placeholder: "Select",
                            }}
                            helperTextProps={{
                                error: !!validationErrors?.defaultRoleId,
                                label: validationErrors?.defaultRoleId
                                    ? validationErrors.defaultRoleId[0]
                                    : "",
                            }}
                        />
                    </FormInputContainer>
                </>
            ) : null}
        </>
    );

    const buildHelperTextPropsLabel = () => {
        let errorMessage = "";

        if (validationErrors?.defaultTeamId) {
            errorMessage = validationErrors.defaultTeamId[0];
        } else if (validationErrors?.defaultRoleId) {
            errorMessage = validationErrors.defaultRoleId[0];
        }

        return errorMessage;
    };

    const oidcTabContent = (
        <>
            <FormInputContainer>
                <FormLabelContainer>
                    <p>OIDC</p>
                    <FormHelperIcon href={oidcDocsUrl}>
                        Enable OIDC to allow new users to join your organization
                        and sign in using OIDC.
                    </FormHelperIcon>
                </FormLabelContainer>

                <StyledSwitch
                    state={oidcSSOEnabled}
                    onChange={handleChangeOidcSso}
                    label={oidcSSOEnabled ? "On" : "Off"}
                />
            </FormInputContainer>

            {oidcSSOEnabled && (
                <>
                    <FormInputContainer>
                        <FormLabelContainer>
                            <StyledP>Authority</StyledP>
                            <FormHelperIcon href={oidcDocsUrl}>
                                Enter the URL of your OIDC identity provider.
                            </FormHelperIcon>
                        </FormLabelContainer>

                        <StyledTextField
                            id="oidc-authority-input"
                            placeholder="Edit"
                            variant="standard"
                            fullWidth={true}
                            value={oidcIssuer}
                            onChange={setOidcIssuer}
                            helperText={validationErrors?.issuer}
                            error={!!validationErrors?.issuer}
                        />
                    </FormInputContainer>

                    <FormInputContainer>
                        <FormLabelContainer>
                            <StyledP>Client ID</StyledP>
                            <FormHelperIcon href={oidcDocsUrl}>
                                Enter the Client ID provided to you by your
                                identity provider during registration.
                            </FormHelperIcon>
                        </FormLabelContainer>

                        <StyledTextField
                            isSsoGroup={false}
                            id="oidc-client-id-input"
                            placeholder="Edit"
                            variant="standard"
                            fullWidth={true}
                            value={oidcClientId}
                            onChange={setOidcClientId}
                            helperText={validationErrors?.clientId}
                            error={!!validationErrors?.clientId}
                        />
                    </FormInputContainer>

                    <SectionLabel>Access</SectionLabel>

                    <FormInputContainer>
                        <FormLabelContainer>
                            <StyledP>Domain</StyledP>
                            <FormHelperIcon href={oidcDocsUrl}>
                                Users with an email address on this domain will
                                be able to sign in via SSO. This must match your
                                email address in Formant. For example, if your
                                email is admin@xyz.com, you can only add
                                xyz.com.
                            </FormHelperIcon>
                        </FormLabelContainer>

                        <StyledTextField
                            id="oidc-domain-input"
                            disabled={true}
                            placeholder="Edit"
                            variant="standard"
                            fullWidth={true}
                            value={oidcDomain}
                            onChange={setOidcDomain}
                            sx={{
                                "& .MuiInputBase-input.Mui-disabled": {
                                    WebkitTextFillColor:
                                        "var(--primary-module, #fff)",
                                },
                            }}
                            helperText={
                                validationErrors?.domain ? (
                                    validationErrors.domain
                                ) : isDomainDuplicated ? (
                                    <StyledDomainError>
                                        Already configured for OIDC.
                                        <Link
                                            component="button"
                                            onClick={showMessenger}
                                        >
                                            Contact support
                                        </Link>
                                    </StyledDomainError>
                                ) : null
                            }
                            error={!!validationErrors?.domain}
                        />
                    </FormInputContainer>

                    <FormInputContainer>
                        <FormLabelContainer>Default access</FormLabelContainer>
                        <FormHelperIcon href={oidcDocsUrl}>
                            Choose the default access level for new users. If
                            the user does not belong to a team as designated in
                            Customize Access, that user will be assigned the
                            default access level.
                        </FormHelperIcon>

                        <CommonGroupPicker
                            id="common-group-picker"
                            groups={groups}
                            options={[...teams, ...roles].map(option =>
                                "roleId" in option
                                    ? {
                                          ...option,
                                          group: "teams",
                                      }
                                    : {
                                          ...option,
                                          group: "roles",
                                      },
                            )}
                            optionIdKey="id"
                            optionLabelKey="name"
                            selectedOption={
                                selectedOIDCRole || selectedOIDCTeam
                            }
                            onSetSelected={value =>
                                value.group === "teams"
                                    ? handleSetSelectedOidcTeam(value)
                                    : handleSetSelectedOidcRole(value)
                            }
                            selectProps={{
                                placeholder: "Search teams & roles",
                            }}
                            helperTextProps={{
                                error:
                                    !!validationErrors?.defaultTeamId ||
                                    !!validationErrors?.defaultRoleId,
                                label: buildHelperTextPropsLabel(),
                            }}
                        />
                    </FormInputContainer>

                    <FormInputContainer>
                        <FormLabelContainer>
                            Customize access
                        </FormLabelContainer>
                        <FormHelperIcon href={oidcDocsUrl}>
                            Enter the names of your OIDC groups, and then map
                            those groups to Teams in Formant.
                        </FormHelperIcon>

                        {ssoGroupToTeam?.length
                            ? ssoGroupToTeam?.map((value, index) => {
                                  const selectedGroupTeam =
                                      teams.find(
                                          team => team.id === value.teamId,
                                      ) || defaultEmptyTeam;
                                  return (
                                      <StyledFormGroupToSsoContainer
                                          key={`oidc-sso-group-${value.id}`}
                                      >
                                          <StyledTextField
                                              isSsoGroup={true}
                                              id={`oidc-sso-group-name-${value.id}`}
                                              key={`oidc-sso-group-name-${value.id}`}
                                              inputRef={
                                                  index ===
                                                  ssoGroupToTeam.length - 1
                                                      ? lastGroupToTeamSsoRef
                                                      : null
                                              }
                                              placeholder=""
                                              variant="standard"
                                              value={value.ssoGroupName}
                                              onChange={(evt: {
                                                  target: {
                                                      value: string | undefined;
                                                  };
                                              }) =>
                                                  handleOnChangeSsoGroup(
                                                      value.id,
                                                      "ssoGroupName",
                                                      evt.target.value,
                                                  )
                                              }
                                          />
                                          <CommonSelectPicker<ITeam>
                                              key={`oidc-sso-group-team-${value.id}`}
                                              onSetSelected={team =>
                                                  handleOnChangeSsoGroup(
                                                      value.id,
                                                      "teamId",
                                                      team.id,
                                                  )
                                              }
                                              optionIdKey="id"
                                              optionLabelKey="name"
                                              options={[...teams]}
                                              selectedOption={selectedGroupTeam}
                                              selectProps={{
                                                  placeholder: "Select",
                                              }}
                                              isPill={true}
                                          />
                                          <IconButton
                                              onClick={() =>
                                                  handleOnDeleteSsoGroupClick(
                                                      value?.id,
                                                  )
                                              }
                                          >
                                              <Icon
                                                  name="delete"
                                                  key={`id-group-${value.ssoGroupName}-${value.teamId}`}
                                                  color="white"
                                              />
                                          </IconButton>
                                      </StyledFormGroupToSsoContainer>
                                  );
                              })
                            : null}
                        <StyledFormGroupToSsoContainer isAddSection={true}>
                            <StyledP
                                style={{ paddingLeft: "10px" }}
                                onClick={handleOnAddSsoGroup}
                            >
                                Add...
                            </StyledP>
                        </StyledFormGroupToSsoContainer>
                    </FormInputContainer>
                </>
            )}
        </>
    );

    const dialogOptions: IDialogOptions[] = [
        {
            tabLabel: "Google",
            tabValue: ssoAuthenticationFlows[1],
            tabContent: googleTabContent,
            tabPanelProps: {
                style: {
                    width: "25rem",
                    maxWidth: "25rem",
                    maxHeight: "56vh",
                    height: "56vh",
                },
            },
        },
    ];

    if (enableSsoOidc) {
        dialogOptions.push({
            tabLabel: "OIDC",
            tabValue: ssoAuthenticationFlows[0],
            tabContent: oidcTabContent,
            tabPanelProps: {
                style: {
                    width: "25rem",
                    maxWidth: "25rem",
                    maxHeight: "56vh",
                    height: "56vh",
                },
            },
        });
    }

    const onSaveSso = useCallback(
        async (type: string) => {
            // clean up previous submission errors
            setValidationErrors(undefined);
            setIsDomainDuplicated(false);
            if (!isSsoAuthenticationFlow(type)) {
                return;
            }

            const ssoData = ssoAuthFlows?.find(
                val => val.authenticationFlow === type,
            );

            try {
                setIsSavingSso(true);

                if (type === "google") {
                    let defaultTeamId = null;
                    if (selectedTeam?.id) {
                        if (selectedTeam.id !== defaultEmptyTeam.id) {
                            defaultTeamId = selectedTeam.id;
                        }
                    }

                    if (ssoData?.id) {
                        const updateBody = {
                            domain,
                            defaultRoleId: selectedRole?.id
                                ? selectedRole.id
                                : null,
                            authenticationFlow: type,
                            defaultTeamId,
                        };
                        await ssoStore.updateSso({
                            id: ssoData?.id,
                            enabled: googleSSOEnabled,
                            // For false values we conditionally send payload information
                            ...(googleSSOEnabled ? updateBody : {}),
                        });
                    } else {
                        await ssoStore.createSso({
                            domain,
                            defaultRoleId: selectedRole?.id
                                ? selectedRole.id
                                : null,
                            authenticationFlow: type,
                            enabled: googleSSOEnabled,
                            defaultTeamId,
                        });
                    }
                    setToggleSso(false);
                }

                if (type === "oidc") {
                    let defaultTeamId = null;
                    let ssoGroupNameToTeamMappings: ISsoGroupToTeamMapping[] =
                        [];

                    if (selectedOIDCTeam?.id) {
                        if (selectedOIDCTeam.id !== defaultEmptyTeam.id) {
                            defaultTeamId = selectedOIDCTeam.id;
                        }
                    }

                    if (ssoGroupToTeam?.length) {
                        ssoGroupNameToTeamMappings = ssoGroupToTeam.map(
                            group => ({
                                ssoGroupName: group.ssoGroupName,
                                teamId: group.teamId,
                            }),
                        );
                    }

                    if (ssoData?.id) {
                        const updateBody = {
                            domain: oidcDomain,
                            defaultRoleId: selectedOIDCRole?.id
                                ? selectedOIDCRole.id
                                : null,
                            authenticationFlow: type,
                            defaultTeamId,
                            clientId: oidcClientId,
                            ssoGroupNameToTeamMappings,
                            issuer: oidcIssuer,
                        };
                        await ssoStore.updateSso({
                            id: ssoData?.id,
                            enabled: oidcSSOEnabled,
                            ...(oidcSSOEnabled ? updateBody : {}),
                        });
                    } else {
                        await ssoStore.createSso({
                            domain: defined(usersDomain),
                            defaultRoleId: selectedOIDCRole?.id
                                ? selectedOIDCRole.id
                                : null,
                            authenticationFlow: type,
                            issuer: oidcIssuer,
                            enabled: oidcSSOEnabled,
                            defaultTeamId,
                            clientId: oidcClientId,
                            ssoGroupNameToTeamMappings,
                        });
                    }
                    setToggleSso(false);
                }
            } catch (error) {
                const responseError = narrowError(error, ResponseError);
                const { statusCode } = responseError;
                if (statusCode === 422) {
                    setValidationErrors(responseError.validationErrors);
                } else if (statusCode === 409) {
                    setIsDomainDuplicated(true);
                } else {
                    throw error;
                }
            } finally {
                setIsSavingSso(false);
            }
        },
        [
            domain,
            oidcDomain,
            selectedRole,
            selectedOIDCRole,
            selectedTeam,
            selectedOIDCTeam,
            googleSSOEnabled,
            oidcSSOEnabled,
            oidcClientId,
            oidcIssuer,
            ssoAuthFlows,
            setIsSavingSso,
            setToggleSso,
            setIsDomainDuplicated,
            setValidationErrors,
            ssoGroupToTeam,
        ],
    );

    return (
        <DialogWithTabs
            onCancel={toggleSsoOff}
            onSave={onSaveSso}
            dialogProps={{ open: toggleSso }}
            dialogTitle="SSO Settings"
            dialogOptions={dialogOptions}
            defaultTabValue={ssoAuthenticationFlows[1]}
            isLoading={isSavingSso}
        />
    );
}

const groups: IPickerGroup<string>[] = [
    { id: "teams", label: "Teams" },
    { id: "roles", label: "Roles" },
];

interface IConnectedSsoDialog {
    toggleSso: boolean;
    setToggleSso: (value: boolean) => void;
}

export const ConnectedSsoDialog = observer(function ConnectedSsoDialog(
    props: IConnectedSsoDialog,
) {
    const { organizationStore, ssoStore, roleStore, teamStore } = useStores();
    const { toggleSso, setToggleSso } = props;
    const ssoAuthFlows = toggleSso ? ssoStore.listAll() : undefined;
    const roles = roleStore.list();
    const teams = teamStore.listAll();
    const organization = organizationStore.authenticated();

    if (
        ssoAuthFlows === undefined ||
        roles === undefined ||
        teams === undefined ||
        !organization
    ) {
        // TODO Loader
        return null;
    }

    // TODO: add support for OIDC integration organization.enableSsoOidc
    const googleIntegration = ssoAuthFlows?.find(
        val => val.authenticationFlow === "google",
    );

    const oidcIntegration = ssoAuthFlows?.find(
        val => val.authenticationFlow === "oidc",
    );

    const selectedRole = roles?.find(
        val => val.id === googleIntegration?.defaultRoleId,
    );

    const selectedTeam =
        teams?.find(val => val.id === googleIntegration?.defaultTeamId) ||
        defaultEmptyTeam;

    const selectedOidcRole = roles?.find(
        val => val.id === oidcIntegration?.defaultRoleId,
    );

    const selectedOidcTeam =
        teams?.find(val => val.id === oidcIntegration?.defaultTeamId) ||
        defaultEmptyTeam;

    const domain = googleIntegration?.domain || "";
    const googleSSOEnabled = !!googleIntegration && googleIntegration.enabled;

    const initialOidcSettings: IInitialSsoSetting = {
        settings: oidcIntegration,
        initialTeam: selectedOidcTeam,
        initialRole: selectedOidcRole,
    };

    return (
        <SsoDialog
            roles={roles}
            teams={teams}
            ssoAuthFlows={ssoAuthFlows}
            toggleSso={toggleSso}
            setToggleSso={setToggleSso}
            enableSsoOidc={organization.enableSsoOidc ?? false}
            initialDomain={domain}
            initialSelectedTeam={selectedTeam}
            initialSelectedRole={selectedRole}
            initialGoogleSSOEnabled={googleSSOEnabled}
            initialOidcSettings={{ ...initialOidcSettings }}
        />
    );
});
